import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';

import Bloco from './componentes/Bloco';
import Colunas from './componentes/Colunas';
import TituloDaTela from './componentes/TituloDaTela';
import BlocoBloqueado from './componentes/BlocoBloqueado';
import NumeroProtagonista from './componentes/NumeroProtagonista';
import ListaDeIndicadores from './componentes/ListaDeIndicadores';
import LinhaDoTempoDoCiclo from './componentes/LinhaDoTempoDoCiclo';
import { ABAS } from './abas';
import { rotuloDaCompetencia } from '../services/loteDoMes';
import { SECOES } from '../diagnostico/catalogo';
import { classificar, classificarTesouraria } from '../diagnostico/faixas';
import { classificarFleuriet, fraseDoCiclo } from '../diagnostico/fleuriet';
import { LARGURA_DE_LEITURA } from './layout';
import {
  estadoDaSecao,
  montarSecao,
  montarIndicador,
  linhaDoTempoDoCiclo,
  formatarPorTipo,
  formatarMoedaCurta
} from './apresentacao';

// O numero protagonista de cada bloco NAO se repete na lista de apoio: ele ja
// esta na dobra, em corpo grande, com faixa e frase.
const PROTAGONISTA_DA_LIQUIDEZ = ['liquidezCorrente'];
const PROTAGONISTA_DO_CICLO = ['ccc'];

/**
 * Tela Caixa e Ciclo — e é AQUI que Fleuriet mora.
 *
 * A tipologia é a leitura mais útil que este produto tem para um consultor e
 * não significa nada para o diretor que abriu a tela: "Tipo 4 de 6" não é
 * resposta para pergunta nenhuma. Ela entra como legenda, ao lado dos três
 * números que a produzem (CDG, NCG e T), depois que a frase já foi lida.
 *
 * Os dois blocos exigem balanço e fecham INTEIROS quando ele falta. Nunca meio
 * bloco: um card cinza no meio de outros preenchidos faz o usuário concluir que
 * o produto está quebrado, e não que falta dado dele.
 */
const TelaCaixaCiclo = ({ leitura, onIrPara }) => {
  const liquidez = estadoDaSecao(leitura, SECOES.LIQUIDEZ);
  const ciclo = estadoDaSecao(leitura, SECOES.CICLO);
  const valores = leitura?.valores || {};
  const referencia = leitura?.referenciaDoBalanco || null;

  const irParaDados = () => {
    if (onIrPara) onIrPara(ABAS.DADOS);
  };

  const liquidezCorrente = montarIndicador(leitura, 'liquidezCorrente');
  const cicloFinanceiro = montarIndicador(leitura, 'ccc');
  const tempoDoCiclo = linhaDoTempoDoCiclo(leitura);
  const tesouraria = classificarTesouraria(valores.saldoTesouraria, valores.receitaLiquida);

  const fleuriet = classificarFleuriet(
    {
      capitalDeGiro: valores.capitalDeGiro,
      ncg: valores.ncg,
      saldoTesouraria: valores.saldoTesouraria
    },
    { balancosDisponiveis: leitura?.cobertura?.comBalanco?.length || 1 }
  );

  const notaDaPosicao = referencia
    ? `Saldos na posição de ${referencia.dataRef || rotuloDaCompetencia(referencia.competencia)}. O balanço mede um instante; o DRE mede um intervalo.`
    : 'O balanço mede um instante, e o DRE mede um intervalo: os saldos abaixo são de uma data, não do período inteiro.';

  return (
    <Stack spacing={3} sx={{ maxWidth: LARGURA_DE_LEITURA, mx: 'auto' }}>
      <TituloDaTela
        titulo="Caixa e Ciclo"
        descricao="Consigo pagar as contas deste mês, e quanto tempo o dinheiro leva para voltar?"
      />
      {/* CAIXA E CICLO LADO A LADO a partir de 1200px: são as duas metades da
          mesma pergunta — tenho dinheiro agora, e quanto tempo ele leva para
          voltar. Empilhados numa tela de 1920 eles deixavam um terço da largura
          vazio de cada lado. */}
      <Colunas apartir="lg" minimo={440} sx={{ alignItems: 'start' }}>
      {liquidez.aberta ? (
        <Bloco
          titulo="Caixa e sobrevivência"
          pergunta="Consigo pagar as contas deste mês?"
          nota={notaDaPosicao}
        >
          <NumeroProtagonista
            rotulo="Liquidez corrente"
            valor={formatarPorTipo(valores.liquidezCorrente, 'ratio')}
            nivel={liquidezCorrente?.nivel}
            rotuloDaFaixa={liquidezCorrente?.rotulo}
            cor={liquidezCorrente?.cor}
            fonte={liquidezCorrente?.fonte}
            leitura={
              valores.liquidezCorrente === null
                ? null
                : `Para cada R$ 1,00 a vencer no curto prazo, a empresa tem ${formatarPorTipo(
                    valores.liquidezCorrente,
                    'ratio'
                  )} em bens e direitos de curto prazo.`
            }
            motivo={liquidezCorrente?.motivo}
            ajuda="Ativo circulante dividido pelo passivo circulante."
          />

          <Box sx={{ mt: 3 }}>
            <ListaDeIndicadores itens={montarSecao(leitura, SECOES.LIQUIDEZ, PROTAGONISTA_DA_LIQUIDEZ)} />
          </Box>
        </Bloco>
      ) : (
        <BlocoBloqueado
          titulo="Caixa e sobrevivência"
          pergunta="Consigo pagar as contas deste mês?"
          chamada={liquidez.chamada}
          campos={liquidez.campos}
          bloqueados={liquidez.bloqueados}
          onDestravar={irParaDados}
        />
      )}

      {ciclo.aberta ? (
        <Bloco
          titulo="Ciclo e capital de giro"
          pergunta="Onde meu dinheiro está preso?"
        >
          <NumeroProtagonista
            rotulo="Ciclo financeiro"
            valor={formatarPorTipo(valores.ccc, 'dias')}
            nivel={cicloFinanceiro?.nivel}
            rotuloDaFaixa={cicloFinanceiro?.rotulo}
            cor={cicloFinanceiro?.cor}
            fonte={cicloFinanceiro?.fonte}
            leitura={fraseDoCiclo(valores.ccc)}
            motivo={cicloFinanceiro?.motivo}
            ajuda="Prazo de estoque mais prazo de recebimento, menos o prazo que o fornecedor concede."
          />

          {tempoDoCiclo && (
            <Box sx={{ mt: 3 }}>
              <LinhaDoTempoDoCiclo dados={tempoDoCiclo} />
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <ListaDeIndicadores itens={montarSecao(leitura, SECOES.CICLO, PROTAGONISTA_DO_CICLO)} />
          </Box>

          {/* A legenda técnica vem DEPOIS da frase, e não no lugar dela. */}
          {fleuriet && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2">Estrutura financeira · Tipo {fleuriet.tipo} de 6</Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Confiança ${fleuriet.confianca}`}
                  color={fleuriet.confianca === 'B' ? 'default' : 'warning'}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`CDG ${fleuriet.sinais.capitalDeGiro} · NCG ${fleuriet.sinais.ncg} · T ${fleuriet.sinais.saldoTesouraria}`}
                />
              </Stack>

              <Typography variant="body2" sx={{ mt: 1, maxWidth: '65ch' }}>
                {fleuriet.frase}
              </Typography>

              <Stack direction="row" spacing={3} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                <Typography variant="caption" color="text.secondary">
                  Capital de giro: <strong>{formatarMoedaCurta(valores.capitalDeGiro)}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Necessidade de giro: <strong>{formatarMoedaCurta(valores.ncg)}</strong>
                </Typography>
                <Typography variant="caption" color={tesouraria.cor === 'error' ? 'error.main' : 'text.secondary'}>
                  Saldo de tesouraria: <strong>{formatarMoedaCurta(valores.saldoTesouraria)}</strong>
                </Typography>
              </Stack>

              {fleuriet.avisoDeConfianca && (
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
                  {fleuriet.avisoDeConfianca} Por isso ela não pontua no Índice de Saúde.
                </Typography>
              )}
            </Box>
          )}
        </Bloco>
      ) : (
        <BlocoBloqueado
          titulo="Ciclo e capital de giro"
          pergunta="Onde meu dinheiro está preso?"
          chamada={ciclo.chamada}
          campos={ciclo.campos}
          bloqueados={ciclo.bloqueados}
          onDestravar={irParaDados}
        />
      )}
      </Colunas>

      {!liquidez.aberta && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1, maxWidth: '75ch' }}>
          Sem balanço, a faixa de saúde destes indicadores não é exibida como zero nem como
          crítica: dado ausente não é dado ruim. {classificar('liquidezCorrente', null).rotulo} é o
          estado correto até os campos serem informados.
        </Typography>
      )}
    </Stack>
  );
};

export default TelaCaixaCiclo;
